import type { ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';

import { OtpModal } from '@/components/auth/otp';
import { useIsUserAuthenticated } from '@/hooks/useIsUserAuthenticated';

import { AuthStrategyContext } from './AuthStrategyContext';
import type { AuthStrategyContextValue, OtpStrategy } from './types';

export const AUTH_CANCELLED_MESSAGE = 'Authentication cancelled';

type OtpStrategyProviderProps = {
  strategy: OtpStrategy;
  children: ReactNode;
};

// gloss
export const OtpStrategyProvider = ({ children, strategy }: OtpStrategyProviderProps) => {
  const isUserAuthenticated = useIsUserAuthenticated();
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [initialEmail, setInitialEmail] = useState('');
  const resolveRef = useRef<(() => void) | null>(null);
  const rejectRef = useRef<((reason?: Error) => void) | null>(null);

  const handleAuth = useCallback(
    (options?: { email?: string }) => {
      if (isUserAuthenticated) return Promise.resolve();
      return new Promise<void>((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
        setInitialEmail(options?.email ?? '');
        setIsOtpModalOpen(true);
      });
    },
    [isUserAuthenticated],
  );

  const handleOtpSuccess = useCallback(() => {
    resolveRef.current?.();
    resolveRef.current = null;
    rejectRef.current = null;
    setTimeout(() => setIsOtpModalOpen(false), 1500);
  }, []);

  const handleOtpClose = useCallback(() => {
    setIsOtpModalOpen(false);
    // why: a closed modal must settle the pending handleAuth() - a caller awaiting it (the testimonial recorder
    // why: holds a finished take behind it) would otherwise hang forever with no way to retry; it rejects, because
    // why: every caller awaits it right before a write and a cancelled sign-in must abort that write
    rejectRef.current?.(new Error(AUTH_CANCELLED_MESSAGE));
    resolveRef.current = null;
    rejectRef.current = null;
  }, []);

  const contextValue: AuthStrategyContextValue = { strategy, handleAuth };

  return (
    <AuthStrategyContext.Provider value={contextValue}>
      {children}
      <OtpModal
        isOpen={isOtpModalOpen}
        initialEmail={initialEmail}
        onSuccess={handleOtpSuccess}
        onClose={handleOtpClose}
        primaryColor={strategy.primaryColor}
      />
    </AuthStrategyContext.Provider>
  );
};
